import React, { useState, useEffect } from 'react';
import { 
  Package, 
  ShoppingCart, 
  Truck, 
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  DollarSign,
  Phone,
  Eye
} from 'lucide-react';
import { Order } from '../../types';
import api from '../../utils/api';
import ShipmentStatusBadge from './ShipmentStatusBadge';

interface OrderStats {
  totalOrders: number;
  pendingOrders: number;
  shippedOrders: number;
  deliveredOrders: number;
  codOrdersPending: number;
  totalRevenue: number;
  recentOrders: Order[];
}

const OrderDashboard: React.FC = () => {
  const [stats, setStats] = useState<OrderStats>({
    totalOrders: 0,
    pendingOrders: 0,
    shippedOrders: 0,
    deliveredOrders: 0,
    codOrdersPending: 0,
    totalRevenue: 0,
    recentOrders: []
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchOrderStats();
  }, []);

  const fetchOrderStats = async () => {
    try {
      const response = await api.get('/orders?limit=50');
      const orders: Order[] = response.data.orders;

      const orderStats = orders.reduce((acc, order) => {
        acc.totalOrders += 1;
        acc.totalRevenue += order.totalAmount;
        
        if (order.orderStatus === 'PLACED' || order.orderStatus === 'CONFIRMED') {
          acc.pendingOrders += 1;
        }
        
        if (order.orderStatus === 'SHIPPED') {
          acc.shippedOrders += 1;
        }
        
        if (order.orderStatus === 'DELIVERED') {
          acc.deliveredOrders += 1;
        }
        
        if (order.paymentMethod === 'COD' && order.paymentStatus === 'PENDING') {
          acc.codOrdersPending += 1;
        }
        
        return acc;
      }, {
        totalOrders: 0,
        pendingOrders: 0,
        shippedOrders: 0,
        deliveredOrders: 0,
        codOrdersPending: 0,
        totalRevenue: 0,
        recentOrders: orders.slice(0, 10)
      });

      setStats(orderStats);
    } catch (error) {
      console.error('Error fetching order stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="p-6">Loading order statistics...</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <Truck className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Shipped</p>
              <p className="text-2xl font-bold text-gray-900">{stats.shippedOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Delivered</p>
              <p className="text-2xl font-bold text-gray-900">{stats.deliveredOrders}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* COD Orders Alert */}
        {stats.codOrdersPending > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <Phone className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-orange-900">COD Orders Pending</h3>
                <p className="text-sm text-orange-800">
                  {stats.codOrdersPending} orders require confirmation calls
                </p>
                <p className="text-2xl font-bold text-orange-900 mt-1">
                  {stats.codOrdersPending}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Revenue Card */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-green-900">Total Revenue</h3>
              <p className="text-sm text-green-800">From all orders</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                ₹{stats.totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Shipment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.recentOrders.map((order) => (
                <tr 
                  key={order._id} 
                  className={`hover:bg-gray-50 ${
                    order.paymentMethod === 'COD' && order.paymentStatus === 'PENDING' 
                      ? 'bg-yellow-50 border-l-4 border-yellow-400' 
                      : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      #{order._id.slice(-8)}
                    </div>
                    {order.paymentMethod === 'COD' && order.paymentStatus === 'PENDING' && (
                      <div className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full inline-block mt-1">
                        📞 Call Required
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {order.shippingAddress.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.guestInfo ? order.guestInfo.email : 'Registered'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ₹{order.totalAmount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      order.orderStatus === 'DELIVERED' ? 'bg-green-100 text-green-800' :
                      order.orderStatus === 'SHIPPED' ? 'bg-purple-100 text-purple-800' :
                      order.orderStatus === 'PROCESSING' ? 'bg-yellow-100 text-yellow-800' :
                      order.orderStatus === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {order.orderStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {order.shipment?.shipmentStatus ? (
                      <div className="space-y-1">
                        <ShipmentStatusBadge status={order.shipment.shipmentStatus} />
                        {order.shipment.courierName && (
                          <div className="text-xs text-gray-500">
                            {order.shipment.courierName}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Not shipped</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {stats.recentOrders.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No orders found
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <button className="flex flex-col items-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
            <ShoppingCart className="h-8 w-8 text-blue-600 mb-2" />
            <span className="text-sm font-medium text-blue-900">View All Orders</span>
          </button>
          
          <button className="flex flex-col items-center p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors">
            <Phone className="h-8 w-8 text-orange-600 mb-2" />
            <span className="text-sm font-medium text-orange-900">COD Calls</span>
          </button>
          
          <button className="flex flex-col items-center p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors">
            <Truck className="h-8 w-8 text-purple-600 mb-2" />
            <span className="text-sm font-medium text-purple-900">Create Shipments</span>
          </button>
          
          <button className="flex flex-col items-center p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">
            <TrendingUp className="h-8 w-8 text-green-600 mb-2" />
            <span className="text-sm font-medium text-green-900">Analytics</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDashboard;